import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Table, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import uuid from 'react-uuid';

import useApi from 'hooks/useApi';
import { saveContactAnswers } from 'redux/actions/notificationReviewActions';
import { showAlert } from 'redux/actions/alertActions';

import { TableHeader, TableHeaderText } from 'shared/components/SharedStyles/SharedStyles';
import ButtonFileUploaders from 'shared/components/ButtonFileUploaders';
import {
  getIconAccessDocument,
  getIconCode,
  getIconDoc,
  getIconExcel,
  getIconImage,
  getIconPdf,
  getIconPpt,
  getIconSound,
  getIconVideo,
  getIconZip,
  toBase64,
  ALERT_TYPE_SUCCESS,
} from 'shared/utils';
import { colors } from 'shared/defaultTheme';
import AlertModal from 'shared/components/AlertModal';
import { LabelError } from 'shared/components/Labels';
import BackgroundDragDrop from 'shared/components/BackgroundDragDrop';
import MainSpinner from 'shared/components/MainSpinner';
import { FileSizeNumber } from 'shared/helpers/fileSize/fileSize';

import { ReactComponent as DocumentIcon } from 'assets/icon-documents.svg';
import { ReactComponent as PdfIcon } from 'assets/icon-pdf.svg';
import { ReactComponent as WordIcon } from 'assets/icon-word.svg';
import { ReactComponent as ImgIcon } from 'assets/icon-img.svg';
import { ReactComponent as ExcelIcon } from 'assets/icon-excel.svg';
import { ReactComponent as HtmlIcon } from 'assets/icon-html.svg';
import { ReactComponent as SoundIcon } from 'assets/icon-sound.svg';
import { ReactComponent as VideoIcon } from 'assets/icon-video.svg';
import { ReactComponent as AccessDocumentIcon } from 'assets/icon-access-document.svg';
import { ReactComponent as PowerPointIcon } from 'assets/icon-ppt.svg';
import { ReactComponent as ZipIcon } from 'assets/icon-zip.svg';
import { ReactComponent as DownloadIcon } from 'assets/icon-download.svg';
import { ReactComponent as DragDropIcon } from 'assets/icon-drag-drop.svg';
import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import useNotification from 'hooks/useNotification';
import { FilesWrapper } from './styles';

const Files = () => {
  const { customAreaApi } = useApi();
  const dispatch = useDispatch();
  const { documents, currentNotificationReviewId } = useSelector(
    (state) => state.notificationReview
  );
  const {
    cusNavId,
    mail,
    authToken,
    clientId,
    isCustomer,
    name: authenticatedName,
  } = useSelector((state) => state.auth);
  const { currentClient } = useSelector((state) => state.clientState);
  const { showTypeNotifications } = useSelector((state) => state.notification);
  const theme = useSelector((state) => state.theme);
  const { mainColor } = theme;
  const { t } = useTranslation();
  const [selecteIndex, setSelecteIndex] = useState();
  const [showError, setShowError] = useState(false);
  const [messageError, setMessageError] = useState('');
  const [uploadedError, setUploadedError] = useState('');

  const { refreshCurrentNotificationReview } = useNotification();

  const [{ loading: loadingDownload }, getDownload] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const [{ loading: loadingMassiveDownload }, getMassiveDownload] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const [{ loading: createAnswerLoading }, createAnswer] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: createMassiveAnswerLoading }, createMassiveAnswer] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: createGeneralContactAnswerLoading }, createGeneralContactAnswer] =
    customAreaApi(
      {
        method: 'POST',
      },
      { manual: true }
    );

  const onToggleError = () => setShowError((currentShow) => !currentShow);

  const onDownload = async (currentDocument, index) => {
    try {
      setSelecteIndex(index);
      const { name } = currentDocument;
      const { data, errors } = !showTypeNotifications
        ? await getDownload({
            url: `${window.API_V1}/cli/notifications/${cusNavId}/${
              currentClient?.clientId || clientId
            }/${encodeURIComponent(mail)}/${currentNotificationReviewId}/download/${
              currentDocument.id
            }`,
          })
        : await getMassiveDownload({
            url: `${window.API_V1}/gen/not/${cusNavId}/${encodeURIComponent(
              mail
            )}/${currentNotificationReviewId}/download/${currentDocument.id}`,
          });
      if (data) {
        const url = !showTypeNotifications
          ? `${window.REACT_APP_SERVER_URL}${window.API_V1}/cli/notifications/${cusNavId}/${
              currentClient?.clientId || clientId
            }/${encodeURIComponent(mail)}/${currentNotificationReviewId}/download/${
              currentDocument.id
            }`
          : `${window.REACT_APP_SERVER_URL}${
              window.API_V1
            }/gen/not/${cusNavId}/${encodeURIComponent(
              mail
            )}/${currentNotificationReviewId}/download/${currentDocument.id}`;
        const result = await fetch(url, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        const blob = await result.blob();
        const tempLink = window.document.createElement('a');
        tempLink.href = window.URL.createObjectURL(blob);
        tempLink.setAttribute('download', name);
        tempLink.click();
      }
      if (errors?.length) {
        throw new Error(
          errors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
    } catch (error) {
      setMessageError(t('custom_field_operation_error_message'));
      onToggleError();
    }
  };

  const onErrorLogoUploading = (error) => {
    setUploadedError(error);
  };

  const answerSave = async (attachments) => {
    try {
      const {
        data: { dataErrors },
      } = !showTypeNotifications
        ? await createAnswer({
            data: [
              {
                id: uuid(),
                text: t('document_shared_file'),
                createdBy: mail,
                docs: attachments,
                createdByName: authenticatedName,
              },
            ],
            url: `${window.API_V1}/cli/notifications/${cusNavId}/${
              currentClient?.clientId || clientId
            }/${currentNotificationReviewId}/answer`,
          })
        : await createMassiveAnswer({
            data: [
              {
                id: uuid(),
                text: t('document_shared_file'),
                createdBy: mail,
                docs: attachments,
                createdByName: authenticatedName,
              },
            ],
            url: `${window.API_V1}/gen/not/${cusNavId}/${currentNotificationReviewId}/answer`,
          });
      if (dataErrors) {
        throw new Error(
          dataErrors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
      await refreshCurrentNotificationReview();
    } catch (error) {
      setShowError(true);
    }
  };

  const answerContactSave = async (attachments) => {
    try {
      const {
        data: { dataErrors, data },
      } = !showTypeNotifications
        ? await createAnswer({
            data: [
              {
                id: uuid(),
                text: t('document_shared_file'),
                createdBy: mail,
                docs: attachments,
                createdByName: authenticatedName,
              },
            ],
            url: `${window.API_V1}/cli/notifications/${cusNavId}/${
              currentClient?.clientId || clientId
            }/${currentNotificationReviewId}/answer`,
          })
        : await createGeneralContactAnswer({
            data: [
              {
                id: uuid(),
                text: t('document_shared_file'),
                createdBy: mail,
                docs: attachments,
                createdByName: authenticatedName,
              },
            ],
            url: `${window.API_V1}/gen/not/${cusNavId}/${currentNotificationReviewId}/answerContact`,
          });
      if (!showTypeNotifications) {
        await refreshCurrentNotificationReview();
      } else {
        dispatch(saveContactAnswers(data.id));
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_SUCCESS,
            alertMessage: t('notification_save_general_contact_answers'),
          })
        );
      }
      if (dataErrors) {
        throw new Error(
          dataErrors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
      await refreshCurrentNotificationReview();
    } catch (error) {
      setShowError(true);
    }
  };

  const saveDocument = async (files) => {
    const newFilesPromises = files.map((file) => {
      return new Promise((resolve) => {
        toBase64(file).then((currentFileBase64) => {
          const type = file.name.split('.');
          resolve({
            id: uuid(),
            name: file.name,
            type: type[type.length - 1],
            size: file.size,
            permissions: [
              {
                u: mail,
                p: 'rw-',
              },
            ],
            data: currentFileBase64,
            created: new Date(),
            isBD: false,
            isValidFile: true,
          });
        });
      });
    });
    Promise.all(newFilesPromises).then((newFiles) => {
      if (isCustomer) {
        answerSave(newFiles);
      } else {
        answerContactSave(newFiles);
      }
    });
  };

  const getUploadSize = (files) => {
    let totalSizeUpload = 0;
    files.forEach((file) => {
      const megaBytes = FileSizeNumber(file.size);
      totalSizeUpload += parseInt(megaBytes, 10);
    });
    if (totalSizeUpload > 124) {
      setMessageError(t('new_document_text_maximum_size_upload'));
      setShowError(true);
      return false;
    }
    return true;
  };

  const getFiles = (files) => {
    if (files.length <= 10) {
      const existFileNull = files.some((x) => x.size === 0);
      if (existFileNull) {
        setMessageError(t('new_document_file_null'));
        setShowError(true);
        return;
      }
      const validateUploadFile = getUploadSize(files);
      if (validateUploadFile) {
        saveDocument(files);
      }
    } else {
      setMessageError(t('document_up_file_uploaded_maximum_size'));
      setShowError(true);
    }
  };

  const IconFile = ({ file }) => {
    if (getIconDoc(file.type)) {
      return <WordIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconPdf(file.type)) {
      return <PdfIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconImage(file.type)) {
      return <ImgIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconCode(file.type)) {
      return <HtmlIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconSound(file.type)) {
      return <SoundIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconVideo(file.type)) {
      return <VideoIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconAccessDocument(file.type)) {
      return (
        <AccessDocumentIcon
          fill={colors.suvaGrey}
          className="control-panel__icon-type"
          width="20"
        />
      );
    }
    if (getIconPpt(file.type)) {
      return (
        <PowerPointIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />
      );
    }
    if (getIconZip(file.type)) {
      return <ZipIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconExcel(file.type)) {
      return <ExcelIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    return <DocumentIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
  };

  IconFile.propTypes = {
    file: PropTypes.shape({
      type: PropTypes.string,
    }).isRequired,
  };

  return (
    <FilesWrapper className="h-100">
      <BackgroundDragDrop
        onChangeFile={(files) => getFiles(files)}
        onError={onErrorLogoUploading}
        className="files-box-container"
      >
        {documents.length > 0 && (
          <div className="control-panel__table">
            <Table className="documentation-results-table">
              <thead>
                <tr>
                  <th>
                    <TableHeader>
                      <TableHeaderText>{t('panel_table_type')}</TableHeaderText>
                    </TableHeader>
                  </th>
                  <th>
                    <TableHeader>
                      <TableHeaderText>{t('panel_table_name')}</TableHeaderText>
                    </TableHeader>
                  </th>
                  <th>
                    <TableHeader>
                      <TableHeaderText>{t('custom_field_date_fields')}</TableHeaderText>
                    </TableHeader>
                  </th>
                  <th>
                    <TableHeader>
                      <TableHeaderText>{t('general_column_action')}</TableHeaderText>
                    </TableHeader>
                  </th>
                </tr>
              </thead>
              <tbody>
                {documents.map((currentDocument, index) => (
                  <tr
                    key={currentDocument.id}
                    role="button"
                    tabIndex={0}
                    aria-label={`${t('general_button_download')}: ${currentDocument.name}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onDownload(currentDocument, index);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        onDownload(currentDocument, index);
                      }
                    }}
                  >
                    <td>
                      <div className="d-flex">
                        <IconFile file={currentDocument} />
                      </div>
                    </td>
                    <td>{currentDocument.name}</td>
                    <td>{moment(currentDocument.created).format('DD/MM/YYYY HH:mm')}</td>
                    <td>
                      <div className="">
                        <>
                          {!loadingDownload || loadingMassiveDownload ? (
                            <button
                              id={`download-icon-document-${index}`}
                              title={t('general_button_download')}
                              aria-label={t('general_button_download')}
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                onDownload(currentDocument, index);
                              }}
                              className="button-icon"
                            >
                              <DownloadIcon fill={colors.suvaGrey} />
                            </button>
                          ) : (
                            <>{selecteIndex === index && <Spinner color={mainColor} />}</>
                          )}
                        </>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {!documents.length && (
          <div
            className="d-flex flex-column justify-content-center align-items-center h-100"
            aria-hidden="true"
            role="button"
          >
            <div className="d-flex flex-column m-2 pt-1">
              <div className="d-flex justify-content-center mb-3">
                <DragDropIcon width="45" />
              </div>
              <span className="text-center notification-drag-text mb-4">
                {t('notification_drag')}
              </span>
            </div>
            <div className="text-center my-3">
              <div className="notification-drag-divider" />
              <span className="notification-explore-computer">
                {t('notification_explore_computer')}
              </span>
              <SearchIcon width="17" className="ml-1" />
            </div>
          </div>
        )}
        {documents.length > 0 && (
          <div
            className="button-upload-footer"
            aria-hidden="true"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <ButtonFileUploaders
              onChangeFile={(files) => getFiles(files)}
              onError={onErrorLogoUploading}
              title={t('general_drag_drop_document')}
              className="file-button-upload"
            />
          </div>
        )}
      </BackgroundDragDrop>
      <AlertModal
        type="error"
        isOpen={showError}
        toggle={onToggleError}
        message={messageError}
        okText={t('general_button_ok')}
        onOk={onToggleError}
      />
      {uploadedError && <LabelError label={uploadedError} />}
      {(createAnswerLoading || createMassiveAnswerLoading || createGeneralContactAnswerLoading) && (
        <MainSpinner />
      )}
    </FilesWrapper>
  );
};

export default Files;
